/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type {AxiosError} from 'axios';
import {toast} from 'react-toastify';
import useLocales from '@/hooks/useLocales';
import useError from '@/hooks/useError';
import LoadingScreen from '@/components/common/LoadingScreen';
import {
  createFormFields,
  getFormFields,
  updateFormFields
} from '@/apis/FormFields';
import {
  createPositionFields,
  getPositionFields,
  updatePositionFields
} from '@/apis/PositionFields';
import {getLinkedApps} from '@/apis/assessment';

type Kind = 'form_field' | 'position_field';

type Rules = {
  blocking_values: string[];
  attention_values: string[];
  not_applicable_values: string[];
  not_applicable: 'OK' | 'ATTENTION' | 'MISSING';
  required: boolean;
  follow_up_required: boolean;
  min: number | null;
  max: number | null;
};

type Source = {kind: Kind; id: number | ''; rules: Rules};

/** Field types an app can assess; the API refuses others too. */
export const SUPPORTED_TYPES = [
  'choice_follow_up',
  'choice',
  'boolean',
  'number',
  'decimal'
];

export const DISPLAY_FIELDS = [
  'show_summary',
  'show_status',
  'show_completeness',
  'show_counts',
  'show_attention_points',
  'show_provenance'
] as const;

const EMPTY_RULES: Rules = {
  blocking_values: [],
  attention_values: [],
  not_applicable_values: [],
  not_applicable: 'OK',
  required: false,
  follow_up_required: false,
  min: null,
  max: null
};

/** The answers of a source field as {value, label}; values are stable keys. */
export const sourceValues = (field: any): {value: string; label: string}[] => {
  if (!field) {
    return [];
  }
  if (field.field_type === 'choice_follow_up') {
    return (field.options ?? []).map((o: any) => ({
      value: o.key,
      label: o.label
    }));
  }
  if (field.field_type === 'boolean') {
    return [
      {value: 'true', label: 'yes'},
      {value: 'false', label: 'no'}
    ];
  }
  return (field.choices ?? [])
    .filter((c: any) => !c.is_deleted)
    .map((c: any) => ({
      value: String(c.id),
      label: c.label ?? c.value ?? c.name ?? String(c.id)
    }));
};

const notDeleted = {
  operation: 'AND',
  predicates: [
    {fieldname: 'is_deleted', lookup: 'exact', negation: false, value: 'false'}
  ]
};

/**
 * Settings of a 'Beoordelingsresultaat' field.
 *
 * Mien does not assess: the chosen linked app reads these sources and rules,
 * makes the assessment and delivers it. Rules use the stable answer keys.
 */
const NewAssessmentResultField = (props: any) => {
  const {translate} = useLocales();
  const {handleErrors} = useError();
  const [label, setLabel] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [sources, setSources] = React.useState<Source[]>([]);
  const [receiveApps, setReceiveApps] = React.useState<number[]>([]);
  const [sendApps, setSendApps] = React.useState<number[]>([]);
  const [display, setDisplay] = React.useState<Record<string, boolean>>(
    Object.fromEntries(DISPLAY_FIELDS.map(key => [key, true]))
  );
  const [apps, setApps] = React.useState<any[]>([]);
  const [fields, setFields] = React.useState<Record<Kind, any[]>>({
    form_field: [],
    position_field: []
  });
  const [loading, setLoading] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const isPositionField =
    props?.type === 'position_field' ||
    window.location.pathname.includes('new_position_field');
  const kinds: Kind[] = isPositionField
    ? ['position_field', 'form_field']
    : ['form_field'];

  React.useEffect(() => {
    getLinkedApps()
      .then(response => setApps(response.data.results ?? response.data))
      .catch(() => setApps([]));
    const supported = (list: any[]) =>
      list.filter((f: any) => SUPPORTED_TYPES.includes(f.field_type));
    getFormFields({filters: notDeleted, page_size: -1})
      .then(response =>
        setFields(prev => ({
          ...prev,
          form_field: supported(response.data.results ?? [])
        }))
      )
      .catch(() => undefined);
    if (isPositionField) {
      getPositionFields({filter: notDeleted, page_size: -1})
        .then(response =>
          setFields(prev => ({
            ...prev,
            position_field: supported(response.data.results ?? [])
          }))
        )
        .catch(() => undefined);
    }
  }, []);

  React.useEffect(() => {
    if (props?.id) {
      setLabel(props.label ?? '');
      setDescription(props.description ?? '');
      setSources(
        (props.sources ?? []).map((s: any) => ({
          ...s,
          rules: {...EMPTY_RULES, ...s.rules}
        }))
      );
      setReceiveApps(props.receive_from_apps ?? []);
      setSendApps(props.send_to_apps ?? []);
      setDisplay(
        Object.fromEntries(
          DISPLAY_FIELDS.map(key => [key, props[key] !== false])
        )
      );
    } else {
      setLabel('');
      setDescription('');
      setSources([]);
    }
  }, [props?.id]);

  const updateSource = (index: number, changes: Partial<Source>) =>
    setSources(prev =>
      prev.map((s, i) => (i === index ? {...s, ...changes} : s))
    );
  const updateRules = (index: number, changes: Partial<Rules>) =>
    setSources(prev =>
      prev.map((s, i) =>
        i === index ? {...s, rules: {...s.rules, ...changes}} : s
      )
    );

  const invalid =
    !label.trim() ||
    sources.length === 0 ||
    sources.some(s => s.id === '') ||
    receiveApps.length === 0;

  const submitField = () => {
    setSubmitted(true);
    props?.setRestoreFieldName?.('');
    if (invalid) {
      return;
    }
    const payload: any = {
      field_type: 'assessment_result',
      label,
      description,
      placeholder: '',
      sources,
      receive_from_apps: receiveApps,
      send_to_apps: sendApps,
      ...display
    };
    const request = props?.id
      ? (isPositionField ? updatePositionFields : updateFormFields)({
          ...payload,
          id: props.id
        })
      : (isPositionField ? createPositionFields : createFormFields)(payload);
    setLoading(true);
    request
      .then(() => {
        setLoading(false);
        setSubmitted(false);
        props?.setUpdated?.(true);
        toast.success(translate('submitted_success'));
      })
      .catch((error: AxiosError<any>) => {
        setLoading(false);
        if (
          error?.response?.data?.non_field_errors?.[0] ===
          'deleted_field_with_provided_label_already_exists'
        ) {
          props?.setRestoreFieldName?.(label);
          toast.error(translate('field_already_exist'));
        } else {
          handleErrors(error?.response?.data);
        }
      });
  };

  const valuesSelect = (
    index: number,
    key: 'blocking_values' | 'attention_values' | 'not_applicable_values',
    options: {value: string; label: string}[]
  ) => (
    <div className="flex flex-col gap-1">
      <FormLabel className="text-xs">
        {translate(`assessment_${key}`)}
      </FormLabel>
      <Select
        multiple
        size="small"
        className="bg-white min-w-[160px]"
        data-testid={`assessment-${key}-${index}`}
        value={sources[index].rules[key]}
        onChange={event =>
          updateRules(index, {
            [key]: (event.target.value as string[]).filter(Boolean)
          })
        }
      >
        {options.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {translate(option.label)}
          </MenuItem>
        ))}
      </Select>
    </div>
  );

  const appsSelect = (
    value: number[],
    setValue: (ids: number[]) => void,
    testId: string
  ) => (
    <Select
      multiple
      size="small"
      className="bg-white min-w-[220px]"
      data-testid={testId}
      value={value}
      onChange={event => setValue(event.target.value as number[])}
    >
      {apps
        .filter(app => app.is_active || value.includes(app.id))
        .map(app => (
          <MenuItem key={app.id} value={app.id}>
            {app.name}
          </MenuItem>
        ))}
    </Select>
  );

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="flex flex-col gap-4" data-testid="new-assessment-field">
      <p className="text-sm text-gray-600">
        {translate('assessment_field_info')}
      </p>
      <TextField
        size="small"
        label={translate('assessment_field_label')}
        value={label}
        error={submitted && !label.trim()}
        onChange={event => setLabel(event.target.value)}
      />
      <TextField
        size="small"
        multiline
        minRows={2}
        label={translate('description')}
        value={description}
        onChange={event => setDescription(event.target.value)}
      />

      <div className="flex flex-col gap-2">
        <FormLabel>{translate('assessment_sources')}</FormLabel>
        <p className="text-xs text-gray-500">
          {translate('assessment_supported_types', {
            types: SUPPORTED_TYPES.map(type =>
              translate(`assessment_type_${type}`)
            ).join(', ')
          })}
        </p>
        {sources.map((source, index) => {
          const field = fields[source.kind].find(
            (f: any) => f.id === source.id
          );
          const values = sourceValues(field);
          const numeric = ['number', 'decimal'].includes(field?.field_type);
          return (
            <div
              key={`${source.kind}-${index}`}
              className="flex flex-col gap-2 rounded-lg border border-gray-200 p-2"
              data-testid={`assessment-source-${index}`}
            >
              <div className="flex flex-wrap items-center gap-2">
                {kinds.length > 1 && (
                  <Select
                    size="small"
                    className="bg-white"
                    value={source.kind}
                    onChange={event =>
                      updateSource(index, {
                        kind: event.target.value as Kind,
                        id: '',
                        rules: EMPTY_RULES
                      })
                    }
                  >
                    {kinds.map(kind => (
                      <MenuItem key={kind} value={kind}>
                        {translate(`assessment_kind_${kind}`)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
                <Select
                  size="small"
                  displayEmpty
                  className="bg-white min-w-[240px]"
                  error={submitted && source.id === ''}
                  value={source.id}
                  data-testid={`assessment-source-field-${index}`}
                  onChange={event =>
                    updateSource(index, {
                      id: Number(event.target.value),
                      rules: EMPTY_RULES
                    })
                  }
                >
                  <MenuItem value="">
                    {translate('assessment_choose_field')}
                  </MenuItem>
                  {fields[source.kind]
                    .filter(
                      (f: any) =>
                        f.id === source.id ||
                        !sources.some(
                          s => s.kind === source.kind && s.id === f.id
                        )
                    )
                    .map((f: any) => (
                      <MenuItem key={f.id} value={f.id}>
                        {f.label} (
                        {translate(`assessment_type_${f.field_type}`)})
                      </MenuItem>
                    ))}
                </Select>
                <IconButton
                  size="small"
                  aria-label={translate('assessment_remove_source')}
                  onClick={() =>
                    setSources(prev => prev.filter((_, i) => i !== index))
                  }
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </div>
              {field && (
                <div className="flex flex-wrap items-end gap-3">
                  {values.length > 0 &&
                    valuesSelect(index, 'blocking_values', values)}
                  {values.length > 0 &&
                    valuesSelect(index, 'attention_values', values)}
                  {values.length > 0 &&
                    valuesSelect(index, 'not_applicable_values', values)}
                  {source.rules.not_applicable_values.length > 0 && (
                    <div className="flex flex-col gap-1">
                      <FormLabel className="text-xs">
                        {translate('assessment_not_applicable')}
                      </FormLabel>
                      <Select
                        size="small"
                        className="bg-white"
                        value={source.rules.not_applicable}
                        onChange={event =>
                          updateRules(index, {
                            not_applicable: event.target
                              .value as Rules['not_applicable']
                          })
                        }
                      >
                        {['OK', 'ATTENTION', 'MISSING'].map(option => (
                          <MenuItem key={option} value={option}>
                            {translate(`assessment_na_${option}`)}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                  )}
                  {numeric &&
                    (['min', 'max'] as const).map(bound => (
                      <TextField
                        key={bound}
                        size="small"
                        type="number"
                        className="w-[110px] bg-white"
                        label={translate(`assessment_${bound}`)}
                        value={source.rules[bound] ?? ''}
                        onChange={event =>
                          updateRules(index, {
                            [bound]:
                              event.target.value === ''
                                ? null
                                : Number(event.target.value)
                          })
                        }
                      />
                    ))}
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={source.rules.required}
                        onChange={event =>
                          updateRules(index, {required: event.target.checked})
                        }
                      />
                    }
                    label={translate('assessment_required')}
                  />
                  {field.field_type === 'choice_follow_up' && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={source.rules.follow_up_required}
                          onChange={event =>
                            updateRules(index, {
                              follow_up_required: event.target.checked
                            })
                          }
                        />
                      }
                      label={translate('assessment_follow_up_required')}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
        {submitted && sources.length === 0 && (
          <p className="text-xs text-error">
            {translate('assessment_sources_required')}
          </p>
        )}
        <div>
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              setSources(prev => [
                ...prev,
                {kind: kinds[0], id: '', rules: EMPTY_RULES}
              ])
            }
          >
            + {translate('assessment_add_source')}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-6">
        <div className="flex flex-col gap-1">
          <FormLabel>{translate('assessment_receive_from_apps')}</FormLabel>
          {appsSelect(receiveApps, setReceiveApps, 'assessment-receive-apps')}
          {submitted && receiveApps.length === 0 && (
            <p className="text-xs text-error">
              {translate('assessment_app_required')}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <FormLabel>{translate('assessment_send_to_apps')}</FormLabel>
          {appsSelect(sendApps, setSendApps, 'assessment-send-apps')}
        </div>
      </div>
      {apps.length === 0 && (
        <p className="text-xs text-gray-500">
          {translate('assessment_no_apps')}
        </p>
      )}

      <div className="flex flex-col gap-1">
        <FormLabel>{translate('assessment_display')}</FormLabel>
        <div className="flex flex-wrap gap-x-4">
          {DISPLAY_FIELDS.map(key => (
            <FormControlLabel
              key={key}
              control={
                <Checkbox
                  size="small"
                  checked={display[key]}
                  onChange={event =>
                    setDisplay(prev => ({...prev, [key]: event.target.checked}))
                  }
                />
              }
              label={translate(`assessment_${key}`)}
            />
          ))}
        </div>
      </div>

      <div>
        <Button variant="contained" onClick={submitField}>
          {translate('save')}
        </Button>
      </div>
    </div>
  );
};

export default NewAssessmentResultField;
